import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  RefreshControl,
  Alert,
  Modal,
  ActivityIndicator,
  Switch,
  SafeAreaView,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { Image } from 'react-native';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');
const chartWidth = width - 40;

// Analytics Data Manager
class AnalyticsManager {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  async getAnalytics(websiteId, timeRange = '7d', forceRefresh = false) {
    const cacheKey = `${websiteId}_${timeRange}`;
    
    if (!forceRefresh && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      // Simulate API call - replace with actual API
      const data = await this.fetchAnalyticsFromAPI(websiteId, timeRange);
      
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      return data;
    } catch (error) {
      console.error('Analytics fetch error:', error);
      throw error;
    }
  }

  async fetchAnalyticsFromAPI(websiteId, timeRange) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Generate sample analytics data
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const now = new Date();
    
    const generateTimeSeriesData = (baseValue, variance = 0.3) => {
      return Array.from({ length: days }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (days - 1 - i));
        const randomFactor = 1 + (Math.random() - 0.5) * variance;
        return {
          date: date.toISOString().split('T')[0],
          value: Math.round(baseValue * randomFactor),
        };
      });
    };

    return {
      overview: {
        totalVisitors: Math.round(12500 + Math.random() * 5000),
        totalPageViews: Math.round(45000 + Math.random() * 15000),
        bounceRate: Math.round(35 + Math.random() * 20),
        avgSessionDuration: Math.round(180 + Math.random() * 120),
      },
      
      visitorsData: {
        labels: generateTimeSeriesData(1800).map(d => {
          const date = new Date(d.date);
          return `${date.getMonth() + 1}/${date.getDate()}`;
        }),
        datasets: [{
          data: generateTimeSeriesData(1800).map(d => d.value),
          color: (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
          strokeWidth: 3,
        }],
      },

      pageViewsData: {
        labels: generateTimeSeriesData(5200).map(d => {
          const date = new Date(d.date);
          return `${date.getMonth() + 1}/${date.getDate()}`;
        }),
        datasets: [{
          data: generateTimeSeriesData(5200).map(d => d.value),
        }],
      },

      topPages: [
        { title: 'Home Page', path: '/', views: 15420, bounceRate: 32 },
        { title: 'About Us', path: '/about', views: 8750, bounceRate: 28 },
        { title: 'Services', path: '/services', views: 6890, bounceRate: 35 },
        { title: 'Contact', path: '/contact', views: 4320, bounceRate: 42 },
        { title: 'Blog', path: '/blog', views: 3890, bounceRate: 25 },
      ],

      trafficSources: [
        { name: 'Organic Search', value: 45, color: '#10B981' },
        { name: 'Direct', value: 25, color: '#667eea' },
        { name: 'Social Media', value: 15, color: '#F59E0B' },
        { name: 'Referral', value: 10, color: '#EF4444' },
        { name: 'Email', value: 5, color: '#8B5CF6' },
      ],

      deviceTypes: [
        { name: 'Desktop', value: 55, color: '#667eea' },
        { name: 'Mobile', value: 35, color: '#10B981' },
        { name: 'Tablet', value: 10, color: '#F59E0B' },
      ],

      performance: {
        performanceScore: 85,
        seoScore: 92,
        accessibilityScore: 78,
        pageLoadTime: 2.3,
        firstContentfulPaint: 1.8,
        largestContentfulPaint: 2.9,
        cumulativeLayoutShift: 0.12,
      },

      realTime: {
        activeUsers: Math.round(15 + Math.random() * 25),
        currentPageViews: Math.round(45 + Math.random() * 35),
        topActivePages: [
          { path: '/', users: Math.round(8 + Math.random() * 12) },
          { path: '/services', users: Math.round(3 + Math.random() * 8) },
          { path: '/about', users: Math.round(2 + Math.random() * 6) },
        ],
      },
    };
  }
}

// Metric Card Component
const MetricCard = ({ title, value, change, icon, color = '#667eea', subtitle }) => {
  const isPositive = change && change > 0;
  const changeColor = isPositive ? '#10B981' : '#EF4444';

  return (
    <View style={[styles.metricCard, { borderLeftColor: color }]}>
      <View style={styles.metricHeader}>
        <View style={styles.metricTitleContainer}>
          <Text style={styles.metricTitle}>{title}</Text>
          {subtitle && <Text style={styles.metricSubtitle}>{subtitle}</Text>}
        </View>
        <View style={[styles.metricIcon, { backgroundColor: color + '20' }]}>
          <Ionicons name={icon} size={20} color={color} />
        </View>
      </View>
      <Text style={styles.metricValue}>{value}</Text>
      {change !== undefined && (
        <View style={styles.metricChange}>
          <Ionicons 
            name={isPositive ? 'trending-up' : 'trending-down'} 
            size={14} 
            color={changeColor} 
          />
          <Text style={[styles.metricChangeText, { color: changeColor }]}>
            {Math.abs(change)}%
          </Text>
        </View>
      )}
    </View>
  );
};

// Chart Container Component
const ChartContainer = ({ title, children, onRefresh, isLoading }) => (
  <View style={styles.chartContainer}>
    <View style={styles.chartHeader}>
      <Text style={styles.chartTitle}>{title}</Text>
      <TouchableOpacity 
        style={styles.refreshButton} 
        onPress={onRefresh}
        disabled={isLoading}
      >
        <Ionicons 
          name="refresh" 
          size={20} 
          color="#667eea" 
          style={isLoading && { opacity: 0.5 }}
        />
      </TouchableOpacity>
    </View>
    {isLoading ? (
      <View style={styles.chartLoading}>
        <ActivityIndicator size={36} color="#667eea" />
      </View>
    ) : (
      children
    )}
  </View>
);

// Real-time Monitor Component
const RealTimeMonitor = ({ data, onToggle, isEnabled }) => (
  <View style={styles.realTimeContainer}>
    <View style={styles.realTimeHeader}>
      <View style={styles.realTimeTitle}>
        <View style={styles.realTimeDot} />
        <Text style={styles.realTimeTitleText}>Real-time</Text>
      </View>
      <Switch
        value={isEnabled}
        onValueChange={onToggle}
        trackColor={{ false: '#E5E5EA', true: '#667eea' }}
        thumbColor="#FFFFFF"
      />
    </View>
    
    {isEnabled && data && (
      <View style={styles.realTimeContent}>
        <View style={styles.realTimeMetrics}>
          <View style={styles.realTimeMetric}>
            <Text style={styles.realTimeMetricValue}>{data.activeUsers}</Text>
            <Text style={styles.realTimeMetricLabel}>Active Users</Text>
          </View>
          <View style={styles.realTimeMetric}>
            <Text style={styles.realTimeMetricValue}>{data.currentPageViews}</Text>
            <Text style={styles.realTimeMetricLabel}>Page Views</Text>
          </View>
        </View>
        
        <View style={styles.realTimePages}>
          <Text style={styles.realTimePagesTitle}>Top Active Pages</Text>
          {data.topActivePages.map((page, index) => (
            <View key={index} style={styles.realTimePage}>
              <Text style={styles.realTimePagePath}>{page.path}</Text>
              <Text style={styles.realTimePageUsers}>{page.users} users</Text>
            </View>
          ))}
        </View>
      </View>
    )}
  </View>
);

// Performance Score Component
const PerformanceScore = ({ score, label, color }) => {
  const radius = 30;
  const strokeWidth = 6;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <View style={styles.performanceScore}>
      <View style={styles.performanceCircle}>
        <Text style={styles.performanceScoreText}>{score}</Text>
      </View>
      <Text style={styles.performanceLabel}>{label}</Text>
    </View>
  );
};

// Main Analytics Dashboard Component
const WebsiteAnalyticsDashboard = ({ route, navigation }) => {
  const { websiteId, websiteName, companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route.params || {};
  
  const [analyticsData, setAnalyticsData] = useState(null);

  const { ask: askAI, agents: aiAgents, loading: aiLoading } = useAI('WebsiteAnalyticsDashboard');
  const [showAIChat, setShowAIChat] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');
  const [showTimeRangePicker, setShowTimeRangePicker] = useState(false);
  const [realTimeEnabled, setRealTimeEnabled] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);

  // Company profile state — route params take priority, then AsyncStorage
  const [companyId, setCompanyId] = useState(routeCompanyId || null);
  const [companyProfile, setCompanyProfile] = useState(routeCompanyProfile || null);
  const [companyLogo, setCompanyLogo] = useState(
    routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo || routeCompanyProfile?.banner_image_url || null
  );

  // Load company profile from storage if not provided via route
  useEffect(() => {
    if (companyProfile && companyId) return; // already have it from route
    (async () => {
      try {
        if (!companyId) {
          let id = await AsyncStorage.getItem('company_id');
          if (!id) id = await AsyncStorage.getItem('company_id');
          if (!id) {
            const p = await AsyncStorage.getItem('company_profile');
            if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
          }
          if (id) setCompanyId(id);
        }

        if (!companyProfile) {
          const cached = await AsyncStorage.getItem('company_profile');
          if (cached) {
            const profile = JSON.parse(cached);
            setCompanyProfile(profile);
            setCompanyLogo(profile?.logo || profile?.logo_url || profile?.company_logo || profile?.banner_image_url || null);
          }
        }
      } catch (e) {
        console.warn('Error loading company profile for analytics:', e);
      }
    })();
  }, []);

  const analyticsManager = useMemo(() => new AnalyticsManager(), []);

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
  ];

  // Load analytics data
  const loadAnalytics = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      const data = await analyticsManager.getAnalytics(websiteId || 'default', timeRange, forceRefresh);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Load analytics error:', error);
      Alert.alert('Error', 'Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  }, [analyticsManager, websiteId, timeRange]);

  // Refresh analytics
  const refreshAnalytics = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await loadAnalytics(true);
    } catch (error) {
      console.error('Refresh analytics error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadAnalytics]);

  // Load data on mount and time range change
  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Real-time data updates
  useEffect(() => {
    if (!realTimeEnabled || !analyticsData) return;

    const interval = setInterval(() => {
      if (analyticsData?.realTime) {
        setAnalyticsData(prev => ({
          ...prev,
          realTime: {
            ...prev.realTime,
            activeUsers: Math.max(1, prev.realTime.activeUsers + Math.round((Math.random() - 0.5) * 6)),
            currentPageViews: Math.max(1, prev.realTime.currentPageViews + Math.round((Math.random() - 0.5) * 10)),
          },
        }));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [realTimeEnabled, analyticsData]);

  const chartConfig = {
    backgroundColor: '#FFFFFF',
    backgroundGradientFrom: '#FFFFFF',
    backgroundGradientTo: '#FFFFFF',
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(142, 142, 147, ${opacity})`,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: '4',
      strokeWidth: '2',
      stroke: '#667eea',
    },
  };

  // Render Header with Back Button
  const renderHeader = () => (
    <LinearGradient
      colors={['#667eea', '#764ba2']}
      style={styles.header}
    >
      <SafeAreaView>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerTitleContainer}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 26, height: 26, borderRadius: 13, marginRight: 8, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
              ) : null}
              <Text style={styles.headerTitle}>Analytics</Text>
            </View>
            <Text style={styles.headerSubtitle}>{companyProfile?.company_name || companyProfile?.name || websiteName || 'Website Analytics'}</Text>
          </View>
          
          <TouchableOpacity
            style={styles.timeRangeButton}
            onPress={() => setShowTimeRangePicker(true)}
          >
            <Text style={styles.timeRangeButtonText}>
              {timeRangeOptions.find(option => option.value === timeRange)?.label}
            </Text>
            <Ionicons name="chevron-down" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  if (isLoading && !analyticsData) {
    return (
      <View style={styles.container}>
        {renderHeader()}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#667eea" />
          <Text style={styles.loadingText}>Loading analytics...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader()}
      
      <ScrollView 
        style={styles.scrollContainer}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshAnalytics}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Real-time Monitor */}
        <RealTimeMonitor
          data={analyticsData?.realTime}
          isEnabled={realTimeEnabled}
          onToggle={setRealTimeEnabled}
        />

        {/* Overview Metrics */}
        <View style={styles.metricsGrid}>
          <MetricCard
            title="Visitors"
            value={analyticsData?.overview.totalVisitors.toLocaleString()}
            change={12.5}
            icon="people"
            color="#667eea"
          />
          <MetricCard
            title="Page Views"
            value={analyticsData?.overview.totalPageViews.toLocaleString()}
            change={8.3}
            icon="eye"
            color="#10B981"
          />
          <MetricCard
            title="Bounce Rate"
            value={`${analyticsData?.overview.bounceRate}%`}
            change={-2.1}
            icon="exit"
            color="#F59E0B"
          />
          <MetricCard
            title="Avg. Session"
            value={`${Math.floor(analyticsData?.overview.avgSessionDuration / 60)}m ${analyticsData?.overview.avgSessionDuration % 60}s`}
            change={15.7}
            icon="time"
            color="#8B5CF6"
          />
        </View>

        {/* Visitors Chart */}
        <ChartContainer
          title="Visitors Over Time"
          onRefresh={() => loadAnalytics(true)}
          isLoading={false}
        >
          <LineChart
            data={analyticsData?.visitorsData}
            width={chartWidth}
            height={220}
            chartConfig={chartConfig}
            bezier
            style={styles.chart}
          />
        </ChartContainer>

        {/* Page Views Chart */}
        <ChartContainer
          title="Page Views"
          onRefresh={() => loadAnalytics(true)}
          isLoading={false}
        >
          <BarChart
            data={analyticsData?.pageViewsData}
            width={chartWidth}
            height={220}
            chartConfig={chartConfig}
            style={styles.chart}
            showValuesOnTopOfBars
          />
        </ChartContainer>

        {/* Traffic Sources */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Traffic Sources</Text>
          <View style={styles.trafficSourcesContainer}>
            {analyticsData?.trafficSources.map((source, index) => (
              <View key={index} style={styles.trafficSource}>
                <View style={styles.trafficSourceInfo}>
                  <View style={[styles.trafficSourceColor, { backgroundColor: source.color }]} />
                  <Text style={styles.trafficSourceName}>{source.name}</Text>
                </View>
                <Text style={styles.trafficSourceValue}>{source.value}%</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Top Pages */}
        <View style={styles.topPagesContainer}>
          <Text style={styles.sectionTitle}>Top Pages</Text>
          {analyticsData?.topPages.map((page, index) => (
            <View key={index} style={styles.topPageItem}>
              <View style={styles.topPageInfo}>
                <Text style={styles.topPageTitle}>{page.title}</Text>
                <Text style={styles.topPagePath}>{page.path}</Text>
              </View>
              <View style={styles.topPageViews}>
                <Text style={styles.topPageViewsText}>{page.views.toLocaleString()}</Text>
                <Text style={styles.topPageViewsLabel}>views</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Performance Metrics */}
        <View style={styles.performanceContainer}>
          <Text style={styles.sectionTitle}>Performance</Text>
          
          <View style={styles.performanceScores}>
            <PerformanceScore
              score={analyticsData?.performance.performanceScore}
              label="Performance"
              color="#10B981"
            />
            <PerformanceScore
              score={analyticsData?.performance.seoScore}
              label="SEO"
              color="#667eea"
            />
            <PerformanceScore
              score={analyticsData?.performance.accessibilityScore}
              label="Accessibility"
              color="#F59E0B"
            />
          </View>

          <View style={styles.performanceMetrics}>
            <View style={styles.performanceMetric}>
              <Text style={styles.performanceMetricLabel}>Page Load Time</Text>
              <Text style={styles.performanceMetricValue}>
                {analyticsData?.performance.pageLoadTime}s
              </Text>
            </View>
            <View style={styles.performanceMetric}>
              <Text style={styles.performanceMetricLabel}>First Contentful Paint</Text>
              <Text style={styles.performanceMetricValue}>
                {analyticsData?.performance.firstContentfulPaint}s
              </Text>
            </View>
            <View style={styles.performanceMetric}>
              <Text style={styles.performanceMetricLabel}>Largest Contentful Paint</Text>
              <Text style={styles.performanceMetricValue}>
                {analyticsData?.performance.largestContentfulPaint}s
              </Text>
            </View>
            <View style={styles.performanceMetric}>
              <Text style={styles.performanceMetricLabel}>Cumulative Layout Shift</Text>
              <Text style={styles.performanceMetricValue}>
                {analyticsData?.performance.cumulativeLayoutShift}
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Time Range Picker Modal */}
      <Modal
        visible={showTimeRangePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowTimeRangePicker(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowTimeRangePicker(false)}
        >
          <View style={styles.timeRangeModal}>
            <Text style={styles.timeRangeModalTitle}>Select Time Range</Text>
            {timeRangeOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.timeRangeOption,
                  timeRange === option.value && styles.timeRangeOptionSelected,
                ]}
                onPress={() => {
                  setTimeRange(option.value);
                  setShowTimeRangePicker(false);
                }}
              >
                <Text
                  style={[
                    styles.timeRangeOptionText,
                    timeRange === option.value && styles.timeRangeOptionTextSelected,
                  ]}
                >
                  {option.label}
                </Text>
                {timeRange === option.value && (
                  <Ionicons name="checkmark" size={20} color="#667eea" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>
    
      {/* AI Assistant */}
      <TouchableOpacity 
        onPress={() => setShowAIChat(true)} 
        style={{ position:'absolute', bottom:20, right:20, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="WebsiteAnalyticsDashboard" visible={showAIChat} onClose={() => setShowAIChat(false)} title="Analytics AI" extraContext={{}} />

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },

  // Header Styles
  header: {
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  timeRangeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 4,
  },
  timeRangeButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },

  // Loading Styles
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#8E8E93',
    marginTop: 16,
  },

  // Scroll Container
  scrollContainer: {
    flex: 1,
  },

  // Real-time Monitor
  realTimeContainer: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    marginBottom: 0,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  realTimeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  realTimeTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  realTimeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#10B981',
  },
  realTimeTitleText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  realTimeContent: {
    gap: 16,
  },
  realTimeMetrics: {
    flexDirection: 'row',
    gap: 16,
  },
  realTimeMetric: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
  },
  realTimeMetricValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  realTimeMetricLabel: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4,
  },
  realTimePages: {
    gap: 8,
  },
  realTimePagesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  realTimePage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
  },
  realTimePagePath: {
    fontSize: 14,
    color: '#1C1C1E',
    fontFamily: 'monospace',
  },
  realTimePageUsers: {
    fontSize: 12,
    color: '#8E8E93',
    fontWeight: '600',
  },

  // Metrics Grid
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 20,
    paddingTop: 20,
    gap: 12,
  },
  metricCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    width: (width - 52) / 2,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metricTitleContainer: {
    flex: 1,
  },
  metricTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#8E8E93',
  },
  metricSubtitle: {
    fontSize: 12,
    color: '#C7C7CC',
    marginTop: 2,
  },
  metricIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  metricChange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricChangeText: {
    fontSize: 12,
    fontWeight: '600',
  },

  // Chart Styles
  chartContainer: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    marginBottom: 0,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  chartHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  chartTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  refreshButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartLoading: {
    height: 220,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },

  // Traffic Sources
  trafficSourcesContainer: {
    gap: 12,
  },
  trafficSource: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  trafficSourceInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  trafficSourceColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  trafficSourceName: {
    fontSize: 14,
    color: '#1C1C1E',
  },
  trafficSourceValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
  },

  // Top Pages
  topPagesContainer: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    marginBottom: 0,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  topPageItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  topPageInfo: {
    flex: 1,
  },
  topPageTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  topPagePath: {
    fontSize: 12,
    color: '#8E8E93',
    fontFamily: 'monospace',
    marginTop: 2,
  },
  topPageViews: {
    alignItems: 'flex-end',
  },
  topPageViewsText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#667eea',
  },
  topPageViewsLabel: {
    fontSize: 10,
    color: '#8E8E93',
  },

  // Performance
  performanceContainer: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  performanceScores: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 24,
  },
  performanceScore: {
    alignItems: 'center',
    gap: 8,
  },
  performanceCircle: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  performanceScoreText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  performanceLabel: {
    fontSize: 12,
    color: '#8E8E93',
    fontWeight: '600',
  },
  performanceMetrics: {
    gap: 12,
  },
  performanceMetric: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  performanceMetricLabel: {
    fontSize: 14,
    color: '#1C1C1E',
  },
  performanceMetricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeRangeModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    width: width - 80,
    maxWidth: 300,
  },
  timeRangeModalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 16,
    textAlign: 'center',
  },
  timeRangeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 8,
  },
  timeRangeOptionSelected: {
    backgroundColor: '#F2F2F7',
  },
  timeRangeOptionText: {
    fontSize: 16,
    color: '#1C1C1E',
  },
  timeRangeOptionTextSelected: {
    fontWeight: '600',
    color: '#667eea',
  },
});

export default WebsiteAnalyticsDashboard;